import { TURN_PHASES } from "./coachThread";

/*
 * What a failed `POST /messages` means for the turn and the screen
 * (contract §6, coach-phase-2-api-s2.md §3). The screen never shows the
 * server `message`: only the copy chosen here from `code`.
 *
 * Outcomes:
 *   - `turn`: the message reached the server (or may have); the bubble stays
 *     with a phase, and Retry resends the same body when `retryable`;
 *   - `rejected`: the server refused it before storing anything (limit,
 *     budget, consent, availability, validation): the bubble is removed and the
 *     text goes back into the composer;
 *   - `regenerate`: client id collision; resend once under a fresh id.
 */

const MODEL_FAILURES = new Set(["COACH_TIMEOUT", "COACH_INVALID_RESPONSE", "COACH_UNAVAILABLE"]);
const HOME_STALE = new Set(["COACH_CONSENT_REQUIRED", "COACH_CONSENT_VERSION_MISMATCH", "COACH_NOT_AVAILABLE"]);

export function classifyMessageError(error) {
  const code = error?.code || null;
  const details = error?.details || {};

  if (code === "COACH_DAILY_MESSAGE_LIMIT") {
    return { kind: "rejected", reason: "LIMIT", limits: details };
  }
  if (code === "COACH_BUDGET_EXHAUSTED") return { kind: "rejected", reason: "BUDGET" };
  if (HOME_STALE.has(code)) return { kind: "rejected", reason: "HOME_STALE" };
  if (code === "VALIDATION_ERROR") return { kind: "rejected", reason: "VALIDATION", fields: details.fields || [] };
  if (error?.status === 401) return { kind: "rejected", reason: "AUTH" };
  if (code === "COACH_CLIENT_MESSAGE_ID_REUSED") return { kind: "regenerate" };
  if (code === "COACH_REPLY_RETRY_EXHAUSTED") {
    return { kind: "turn", phase: TURN_PHASES.EXHAUSTED, retryable: false, failureCode: code };
  }
  if (code === "COACH_MESSAGE_IN_PROGRESS") {
    return { kind: "turn", phase: TURN_PHASES.WAITING, retryable: true, failureCode: null, userEventId: details.userEventId || null };
  }
  if (MODEL_FAILURES.has(code)) {
    // No userEventId: refused before admission (coach unavailable for now).
    if (!details.userEventId) return { kind: "rejected", reason: "HOME_STALE" };
    return { kind: "turn", phase: TURN_PHASES.FAILED, retryable: details.retryable !== false, failureCode: code };
  }
  // Network loss, client timeout, database or unexpected server error: the
  // server may or may not have the message. Resending the same body is safe.
  return { kind: "turn", phase: TURN_PHASES.UNSENT, retryable: true, failureCode: code || "NETWORK" };
}

export function validationCopy(fields = []) {
  const issue = fields.find((field) => field.field === "text")?.issue;
  if (issue === "EMPTY") return "Write a message first.";
  if (issue === "TOO_LONG") return "Keep it under 1,000 characters.";
  return "This message can't be sent. Check it and try again.";
}

// Copy under a user bubble, by status (and the failure that caused it).
export function turnStatusCopy({ status, failureCode }) {
  switch (status) {
    case TURN_PHASES.UNSENT:
      return "Not delivered. Check your connection.";
    case TURN_PHASES.FAILED:
      return failureCode === "COACH_TIMEOUT"
        ? "The coach took too long to reply."
        : "The coach couldn't reply this time.";
    case TURN_PHASES.EXHAUSTED:
      return "This message can't get a reply. Try writing it again.";
    default:
      return null;
  }
}
